import { Injectable } from '@angular/core';
import { DataStoreService } from '../data-store.service';
import { AlleCoreRest, StoreServiceLibrary } from '@wearenovae/novae-core-services';
import { environment } from 'src/environments/environment';
import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config';
import { getApps, initializeApp } from 'firebase/app';
import { UtilsResource } from 'src/app/resources/UtilsResource.resource';
import { generalKeys } from 'src/app/resources/keys.resources';


type infoSource = 'firebase' | 'environment' | 'cache';

interface CachedRemoteData {
  fPU: string;
  fPUJ: string;
  fPUU: string;
  lToG?: string;
  sToG?: string;
  uJU?: string;
  uJP?: string;
  sK?: string;
  tC?: string;
}

export interface RemoteSourceConfig {
  fPU: string | null;
  fPUJ: string | null;
  fPUU: string | null;
  lToG: string | null;
  sToG: string | null;
  uJU: string | null;
  uJP: string | null;
  sK: string | null;
  tC: string | null;
  source: {
    fPU: infoSource;
    fPUJ: infoSource;
    fPUU: infoSource;
    lToG: infoSource;
    sToG: infoSource;
    uJU: infoSource;
    uJP: infoSource;
    sK: infoSource;
    tC: infoSource;
  };
}

@Injectable({ providedIn: 'root' })
export class RemoteConfigService {
  private readonly minimumFetchIntervalInSeconds = 0;

  private remoteSourceConfig: RemoteSourceConfig = 
  {
    fPU: null,
    fPUJ: null,
    fPUU: null,
    lToG: null,
    sToG: null,
    uJU: null,
    uJP: null,
    sK: null,
    tC: null,
    source: {
      fPU: 'environment',
      fPUJ: 'environment',
      fPUU: 'environment',
      lToG: 'environment',
      sToG: 'environment',
      uJU: 'environment',
      uJP: 'environment',
      sK: 'environment',
      tC: 'environment'
    }
  };

  constructor(
        public dataStoreService: DataStoreService
      , private storeServiceLibrary: StoreServiceLibrary
  ) { }

  private readCachedRemoteData(channel: any): CachedRemoteData | null {
    try {
      const rawCache = this.storeServiceLibrary.getItemFromStorage(generalKeys.remoteConfig);

      if (!rawCache) {
        return null;
      }

      const parsedCache = JSON.parse(rawCache);
      return {
        fPU: parsedCache?.fPU || '',
        fPUJ: parsedCache?.fPUJ || '',
        fPUU: parsedCache?.fPUU || '',
        lToG: parsedCache?.lToG || '',
        sToG: parsedCache?.sToG || '',
        uJU: parsedCache?.uJU || '',
        uJP: parsedCache?.uJP || '',
        sK: parsedCache?.sK || '',
        tC: parsedCache?.tC || ''
      };
    } catch (error) {
      UtilsResource.ShowLog('[RemoteConfig] - readCachedRemoteData - catch', error);
      return null;
    }
  }

  private saveCachedRemoteData(channel: any): void {
    try {
      this.remoteSourceConfig.fPU = channel?.fPU || '';
      this.remoteSourceConfig.fPUJ = channel?.fPUJ || '';
      this.remoteSourceConfig.fPUU = channel?.fPUU || '';
      this.remoteSourceConfig.lToG = channel?.lToG || '';
      this.remoteSourceConfig.sToG = channel?.sToG || '';
      this.remoteSourceConfig.uJU = channel?.uJU || '';
      this.remoteSourceConfig.uJP = channel?.uJP || '';
      this.remoteSourceConfig.sK = (environment as any).sK || '';
      this.remoteSourceConfig.tC = channel?.proxyConfig?.tC || '';

      this.storeServiceLibrary.setItemInStorage(generalKeys.remoteConfig, JSON.stringify(this.remoteSourceConfig));
      UtilsResource.ShowLog('[RemoteConfig] - saveCachedRemoteData', this.remoteSourceConfig);
       
      this.setLibraryValues();
    } catch (error) {
      UtilsResource.ShowLog('[RemoteConfig] - saveCachedRemoteData - catch', error);
    }
  }

  private applyCachedRemoteData(channel: any): void {
    const cachedRemoteData = this.readCachedRemoteData(channel);
    if (!cachedRemoteData) {
      UtilsResource.ShowLog('[RemoteConfig] - applyCachedRemoteData - No local cache');
      return;
    }

    if (cachedRemoteData.fPU) {
      channel.fPU = cachedRemoteData.fPU;
      this.remoteSourceConfig.source.fPU = 'cache';
    }
    if (cachedRemoteData.fPUJ) {
      channel.fPUJ = cachedRemoteData.fPUJ;
      this.remoteSourceConfig.source.fPUJ = 'cache';
    }
    if (cachedRemoteData.fPUU) {
      channel.fPUU = cachedRemoteData.fPUU;
      this.remoteSourceConfig.source.fPUU = 'cache';
    }
    if (cachedRemoteData.lToG) {
      channel.lToG = cachedRemoteData.lToG;
      this.remoteSourceConfig.source.lToG = 'cache';
    }
    if (cachedRemoteData.sToG) {
      channel.sToG = cachedRemoteData.sToG;
      this.remoteSourceConfig.source.sToG = 'cache';
    }
    if (cachedRemoteData.uJU) {
      channel.uJU = cachedRemoteData.uJU;
      this.remoteSourceConfig.source.uJU = 'cache';
    }
    if (cachedRemoteData.uJP) {
      channel.uJP = cachedRemoteData.uJP;
      this.remoteSourceConfig.source.uJP = 'cache';
    }
    if (cachedRemoteData.sK) {
      (environment as any).sK = cachedRemoteData.sK;
      this.remoteSourceConfig.source.sK = 'cache';
    }
    if (cachedRemoteData.tC) {
      if (!channel.proxyConfig) {
        channel.proxyConfig = {};
      }
      channel.proxyConfig.tC = cachedRemoteData.tC;
      this.remoteSourceConfig.source.tC = 'cache';
    }

    this.saveCachedRemoteData(channel);
  }

  /**
   * Llama a este método ANTES de que se consuman los fPU*.
   * Si falla (sin conexión, plugin no disponible, etc.) el app sigue usando
   * los valores hardcodeados en environment como fallback.
   */
  initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
          const channel = environment.brands[this.dataStoreService.brand];
              
          this.applyCachedRemoteData(channel);
          
          // Inicializar Firebase app solo una vez
          const app =  getApps().length
              ? getApps()[0]
              : initializeApp(channel.firebaseConfig);

          const remoteConfig = getRemoteConfig(app);
          remoteConfig.settings.minimumFetchIntervalMillis = 0; // en ms para Web SDK

          fetchAndActivate(remoteConfig)
          .then((fetchedRemotely) => {
              UtilsResource.ShowLog('[RemoteConfig] - initialize - fetchAndActivate', fetchedRemotely);
              
              const fields = ['fPU', 'fPUJ', 'fPUU', 'lToG', 'sToG', 'uJU', 'uJP', 'sK', 'tC'];
              for (const field of fields) {
                const val = getValue(remoteConfig, field).asString();
                if (val) {
                if (field === 'sK') {
                    (environment as any).sK = val;
                } else if (field === 'tC') {
                    if (!channel.proxyConfig) { channel.proxyConfig = {}; }
                    channel.proxyConfig.tC = val;
                } else {
                    channel[field] = val;
                }
                (this.remoteSourceConfig as any).source[field] = 'firebase';
                }
              }

              this.saveCachedRemoteData(channel);

              resolve();
            }).catch((err) => {
              UtilsResource.ShowLog('[RemoteConfig] - initialize - fetchAndActivate - catch', err);
              reject(err);
            });
      } catch (err) {
        UtilsResource.ShowLog('[RemoteConfig] - initialize - catch', err);
        reject(err);
      }
    });
  }

  getValues(): RemoteSourceConfig {
    const channel = environment.brands[this.dataStoreService.brand];
    const sourceByField: RemoteSourceConfig = {
      fPU: channel.fPU || null,
      fPUJ: channel.fPUJ || null,
      fPUU: channel.fPUU || null,
      lToG: channel.lToG || null,
      sToG: channel.sToG || null,
      uJU: channel.uJU || null,
      uJP: channel.uJP || null,
      sK: (environment as any).sK || null,
      tC: channel.proxyConfig?.tC || null,
      source: this.remoteSourceConfig.source
    };

    UtilsResource.ShowLog('[RemoteConfig] - getValues', sourceByField);
    return sourceByField;
  }

  private setLibraryValues(): void {
    try {      
      const lenthToGenerate = this.remoteSourceConfig.lToG.split('by');
      StoreServiceLibrary.sToG = this.remoteSourceConfig.sToG.slice(Number.parseInt(lenthToGenerate[0])
        , Number.parseInt(lenthToGenerate[0]) + Number.parseInt(lenthToGenerate[1]));

      if (this.remoteSourceConfig.tC) {
        AlleCoreRest.useProxy = true;
        AlleCoreRest.tokenCore = this.remoteSourceConfig.tC;
      }
      UtilsResource.ShowLog('[RemoteConfig] - setLibraryValues'); 
    } catch (error) {
      UtilsResource.ShowLog('[RemoteConfig] - setLibraryValues - Error', error); 
    }
  }
}
